// @ts-check
// Aba Horários e calendário de Configurações > Atendimento (contrato 10 de atendimento, seções 7.2, 7.3 e 24.4): grade
// semanal por departamento (até quatro faixas por dia, de 15 em 15 minutos, sem sobreposição), feriados nacionais do
// ano com "Abrir neste dia" e os feriados, recessos e horários especiais da escola (de um dia ou de uma faixa de dias).
import { html } from 'htm/preact';
import { useEffect, useState } from 'preact/hooks';
import { addServiceCalendarDays, deleteServiceCalendarDay, listServiceCalendar, listServiceTeams, updateServiceTeamSettings } from '../../data/service-desk.js';
import { MAXIMUM_RANGES_PER_DAY } from '../../../../shared/service-desk/service-hours.js';
import { WEEK_DAYS_IN_DISPLAY_ORDER, serviceHoursProblem } from '../../lib/service-settings-rules.js';
import { QUARTER_HOUR_OPTIONS, calendarRangeDayCount } from '../../lib/service-settings-rules.js';
import { formatShortDate } from '../../lib/format.js';
import { CALENDAR_DAY_KINDS } from '../../../../shared/service-desk/ticket-values.js';
import { SettingsSection } from '../settings/settings-pages.js';
import { Button, LightIconButton } from '../../ui/button.js';
import { Skeleton } from '../../ui/feedback.js';
import { Icon } from '../../ui/icon.js';
import { showSuccessSnackbar } from '../../ui/snackbar.js';
import { serviceErrorMessage, showServiceError } from './service-display.js';
import { t } from '../../i18n/index.js';

/** @param {{ value: string, onChange: (value: string) => void, label: string, disabled?: boolean }} props */
function TimeSelect({ value, onChange, label, disabled = false }) {
  return html`<select class="text-input service-time-select" value=${value} aria-label=${label} disabled=${disabled}
    onChange=${(/** @type {Event} */ event) => onChange(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
    ${QUARTER_HOUR_OPTIONS.map((option) => html`<option key=${option} value=${option}>${option}</option>`)}
  </select>`;
}

/** @param {{ teams: any[], canEdit: boolean, onSaved: () => void }} props */
function WeeklyHoursEditor({ teams, canEdit, onSaved }) {
  const [teamId, setTeamId] = useState(teams[0]?.id ?? '');
  const team = teams.find((candidate) => candidate.id === teamId) ?? null;
  const [weekly, setWeekly] = useState(/** @type {Record<string, Array<[string, string]>>} */ (team?.hours?.weekly ?? {}));
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  useEffect(() => {
    setWeekly(JSON.parse(JSON.stringify(team?.hours?.weekly ?? {})));
    setError(null);
  }, [teamId]);
  const problem = serviceHoursProblem(weekly);
  /** @param {string} day @param {Array<[string, string]>} ranges */
  const setDay = (day, ranges) => setWeekly({ ...weekly, [day]: ranges });
  const save = async () => {
    setIsSaving(true);
    setError(null);
    try {
      await updateServiceTeamSettings(teamId, { hours: { timeZone: 'America/Sao_Paulo', weekly } });
      showSuccessSnackbar(t('serviceSettings.saved'));
      onSaved();
    } catch (requestError) {
      setError(serviceErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };
  return html`<div class="service-settings-card" data-testid="service-weekly-hours">
    <label class="form-field"><span class="form-field-label">${t('serviceSettings.hours.team')}</span>
      <select class="text-input" value=${teamId} onChange=${(/** @type {Event} */ event) => setTeamId(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
        ${teams.map((candidate) => html`<option key=${candidate.id} value=${candidate.id}>${candidate.name}</option>`)}
      </select></label>
    <div class="service-week-grid">
      ${WEEK_DAYS_IN_DISPLAY_ORDER.map((day) => {
        const ranges = weekly[day] ?? [];
        return html`<div key=${day} class="service-week-row" data-closed=${ranges.length === 0 ? 'true' : undefined} data-problem=${problem?.day === day ? 'true' : undefined}>
          <span class="service-week-day">${t(`serviceSettings.hours.days.${day}`)}</span>
          <div class="service-week-ranges">
            ${ranges.length === 0 ? html`<span class="input-hint">${t('serviceSettings.hours.closed')}</span>` : null}
            ${ranges.map((range, index) => html`<span key=${index} class="service-week-range">
              <${TimeSelect} value=${range[0]} label=${t('serviceSettings.hours.start')} disabled=${!canEdit}
                onChange=${(value) => setDay(day, ranges.map((candidate, position) => (position === index ? [value, candidate[1]] : candidate)))} />
              <span>${t('serviceSettings.hours.until')}</span>
              <${TimeSelect} value=${range[1]} label=${t('serviceSettings.hours.end')} disabled=${!canEdit}
                onChange=${(value) => setDay(day, ranges.map((candidate, position) => (position === index ? [candidate[0], value] : candidate)))} />
              ${canEdit ? html`<${LightIconButton} icon="IconX" ariaLabel=${t('serviceSettings.hours.removeRange')} onClick=${() => setDay(day, ranges.filter((_, position) => position !== index))} />` : null}
            </span>`)}
          </div>
          ${canEdit && ranges.length < MAXIMUM_RANGES_PER_DAY
            ? html`<${LightIconButton} icon="IconPlus" ariaLabel=${t('serviceSettings.hours.addRange')} onClick=${() => setDay(day, [...ranges, ranges.length ? ['13:00', '18:00'] : ['08:00', '12:00']])} />`
            : null}
        </div>`;
      })}
    </div>
    ${problem ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${t(`serviceDesk.errorReasons.${problem.reason}`)} (${t(`serviceSettings.hours.days.${problem.day}`)})</span></div>` : null}
    ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
    <span class="input-hint">${t('serviceSettings.hours.timeZoneHint')}</span>
    ${canEdit ? html`<div class="service-inline-actions"><${Button} title=${t('serviceSettings.save')} accent="blue" size="small" disabled=${Boolean(problem)} isLoading=${isSaving} onClick=${save} /></div>` : null}
  </div>`;
}

/** @param {{ teams: any[], canEdit: boolean }} props */
function CalendarEditor({ teams, canEdit }) {
  const year = new Date().getFullYear();
  const [days, setDays] = useState(/** @type {any[] | null} */ (null));
  const [fromDay, setFromDay] = useState('');
  const [toDay, setToDay] = useState('');
  const [kind, setKind] = useState('HOLIDAY');
  const [label, setLabel] = useState('');
  const [teamId, setTeamId] = useState('');
  const [specialStart, setSpecialStart] = useState('08:00');
  const [specialEnd, setSpecialEnd] = useState('12:00');
  const [error, setError] = useState(/** @type {string | null} */ (null));
  const [isSaving, setIsSaving] = useState(false);
  const load = () => listServiceCalendar({ from: `${year}-01-01`, to: `${year}-12-31` }).then(setDays).catch(() => setDays([]));
  useEffect(() => {
    load();
  }, []);
  /** @param {Record<string, any>} input */
  const add = async (input) => {
    setIsSaving(true);
    setError(null);
    try {
      await addServiceCalendarDays(/** @type {any} */ (input));
      showSuccessSnackbar(t('serviceSettings.calendar.added'));
      setLabel('');
      setFromDay('');
      setToDay('');
      load();
    } catch (requestError) {
      setError(serviceErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };
  const dayCount = calendarRangeDayCount(fromDay, toDay);
  const national = (days ?? []).filter((day) => day.source === 'NATIONAL');
  const school = (days ?? []).filter((day) => day.source === 'SCHOOL');
  const isOpenedWorkday = (/** @type {string} */ dayKey) => school.some((day) => day.day === dayKey && day.kind === 'WORKDAY');
  return html`<div class="service-calendar" data-testid="service-calendar">
    <div class="service-settings-card">
      <span class="service-settings-list-title">${t('serviceSettings.calendar.nationalTitle', { year })}</span>
      ${days === null
        ? html`<${Skeleton} height=${80} />`
        : html`<ul class="service-settings-list">${national.map((day) => html`<li key=${day.id}>
            <span class="service-calendar-day">${formatShortDate(`${day.day}T12:00:00`)}</span>
            <span class="service-settings-list-title">${day.label}</span>
            <span class="service-pane-title-spacer"></span>
            ${isOpenedWorkday(day.day)
              ? html`<span class="service-purpose-chip" data-purpose="SERVICE">${t('serviceSettings.calendar.openedOnHoliday')}</span>`
              : canEdit ? html`<${Button} title=${t('serviceSettings.calendar.openOnDay')} size="small" variant="secondary" onClick=${() => add({ fromDay: day.day, kind: 'WORKDAY', label: day.label })} />` : null}
          </li>`)}</ul>`}
    </div>
    <div class="service-settings-card">
      <span class="service-settings-list-title">${t('serviceSettings.calendar.schoolTitle')}</span>
      ${(school ?? []).length === 0 ? html`<p class="input-hint">${t('serviceSettings.calendar.noSchoolDays')}</p>` : null}
      <ul class="service-settings-list">${school.map((day) => html`<li key=${day.id}>
        <span class="service-calendar-day">${formatShortDate(`${day.day}T12:00:00`)}</span>
        <span class="service-purpose-chip" data-kind=${day.kind}>${t(`serviceSettings.calendar.kinds.${day.kind}`)}</span>
        <span class="service-settings-list-title">${day.label}</span>
        ${day.hours ? html`<span class="input-hint">${day.hours.map((/** @type {[string, string]} */ range) => `${range[0]} ${t('serviceSettings.hours.until')} ${range[1]}`).join(', ')}</span>` : null}
        ${day.teamId ? html`<span class="input-hint">${teams.find((team) => team.id === day.teamId)?.name ?? ''}</span>` : null}
        <span class="service-pane-title-spacer"></span>
        ${canEdit ? html`<${LightIconButton} icon="IconTrash" ariaLabel=${t('serviceSettings.calendar.remove')} onClick=${async () => {
          try {
            await deleteServiceCalendarDay(day.id);
            load();
          } catch (requestError) {
            showServiceError(requestError);
          }
        }} />` : null}
      </li>`)}</ul>
      ${canEdit
        ? html`<div class="service-calendar-form">
            <div class="form-field-row">
              <label class="form-field"><span class="form-field-label">${t('serviceSettings.calendar.kind')}</span>
                <select class="text-input" value=${kind} onChange=${(/** @type {Event} */ event) => setKind(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
                  ${CALENDAR_DAY_KINDS.filter((value) => value !== 'WORKDAY').map((value) => html`<option key=${value} value=${value}>${t(`serviceSettings.calendar.kinds.${value}`)}</option>`)}
                </select></label>
              <label class="form-field"><span class="form-field-label">${t('serviceSettings.calendar.label')}</span>
                <input class="text-input" value=${label} maxLength=${120} placeholder=${t(`serviceSettings.calendar.labelPlaceholders.${kind}`)}
                  onInput=${(/** @type {Event} */ event) => setLabel(/** @type {HTMLInputElement} */ (event.currentTarget).value)} /></label>
            </div>
            <div class="form-field-row">
              <label class="form-field"><span class="form-field-label">${kind === 'RECESS' ? t('serviceSettings.calendar.fromDay') : t('serviceSettings.calendar.day')}</span>
                <input class="text-input" type="date" value=${fromDay} onInput=${(/** @type {Event} */ event) => setFromDay(/** @type {HTMLInputElement} */ (event.currentTarget).value)} /></label>
              ${kind === 'RECESS'
                ? html`<label class="form-field"><span class="form-field-label">${t('serviceSettings.calendar.toDay')}</span>
                    <input class="text-input" type="date" value=${toDay} min=${fromDay} onInput=${(/** @type {Event} */ event) => setToDay(/** @type {HTMLInputElement} */ (event.currentTarget).value)} /></label>`
                : null}
              <label class="form-field"><span class="form-field-label">${t('serviceSettings.calendar.team')}</span>
                <select class="text-input" value=${teamId} onChange=${(/** @type {Event} */ event) => setTeamId(/** @type {HTMLSelectElement} */ (event.currentTarget).value)}>
                  <option value="">${t('serviceSettings.calendar.allTeams')}</option>
                  ${teams.map((team) => html`<option key=${team.id} value=${team.id}>${team.name}</option>`)}
                </select></label>
            </div>
            ${kind === 'SPECIAL_HOURS'
              ? html`<div class="service-week-range"><span>${t('serviceSettings.calendar.specialHours')}</span>
                  <${TimeSelect} value=${specialStart} onChange=${setSpecialStart} label=${t('serviceSettings.hours.start')} />
                  <span>${t('serviceSettings.hours.until')}</span>
                  <${TimeSelect} value=${specialEnd} onChange=${setSpecialEnd} label=${t('serviceSettings.hours.end')} /></div>`
              : null}
            ${kind === 'RECESS' && dayCount > 0 ? html`<span class="input-hint">${t('serviceSettings.calendar.dayCount', { count: dayCount })}</span>` : null}
            ${dayCount > 120 ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${t('serviceSettings.calendar.tooManyDays')}</span></div>` : null}
            ${error ? html`<div class="form-error" role="alert"><${Icon} name="IconAlertTriangle" size=${14} /><span>${error}</span></div>` : null}
            <div class="service-inline-actions">
              <${Button} title=${t('serviceSettings.calendar.add')} icon="IconPlus" size="small" accent="blue" isLoading=${isSaving}
                disabled=${!fromDay || !label.trim() || dayCount < 1 || dayCount > 120}
                onClick=${() => add({ fromDay, ...(kind === 'RECESS' && toDay ? { toDay } : {}), kind, label: label.trim(), ...(teamId ? { teamId } : {}),
                  ...(kind === 'SPECIAL_HOURS' ? { hours: [[specialStart, specialEnd]] } : {}) })} />
            </div>
          </div>`
        : null}
    </div>
  </div>`;
}

/** @param {{ canEdit: boolean }} props */
export function ServiceSettingsHoursTab({ canEdit }) {
  const [teams, setTeams] = useState(/** @type {any[] | null} */ (null));
  const load = () => listServiceTeams().then(setTeams).catch(() => setTeams([]));
  useEffect(() => {
    load();
  }, []);
  return html`<div class="service-settings-tab-body" data-testid="service-settings-hours">
    <${SettingsSection} title=${t('serviceSettings.hours.title')} description=${t('serviceSettings.hours.description')}>
      ${teams === null ? html`<${Skeleton} height=${240} />` : html`<${WeeklyHoursEditor} teams=${teams} canEdit=${canEdit} onSaved=${load} />`}
    <//>
    <${SettingsSection} title=${t('serviceSettings.calendar.title')} description=${t('serviceSettings.calendar.description')}>
      ${teams === null ? html`<${Skeleton} height=${160} />` : html`<${CalendarEditor} teams=${teams} canEdit=${canEdit} />`}
    <//>
  </div>`;
}
